import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Container,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
  RadialLinearScale,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  TrendingUp,
 
  AttachMoney as DollarSign,
  TrackChanges as Target,
 

 
  Assessment,
} from "@mui/icons-material";
import Layout from "../../components/layout/ChartsLayout";
import config from "../../config";



ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
  RadialLinearScale
);

const theme = createTheme({
  palette: {
    primary: {
      main: "#EB5A3C",
      light: "#FF7A5C",
      dark: "#C54428",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#F8EEDB",
      light: "#FFFAF0",
      dark: "#E6D4B7",
      contrastText: "#333333",
    },
    background: {
      default: "#F8EEDB",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#333333",
      secondary: "#666666",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: "0 4px 20px rgba(235, 90, 60, 0.1)",
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 8px 30px rgba(235, 90, 60, 0.15)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
  },
});

const InvestmentSedule = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { API_BASE_URL } = config;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${API_BASE_URL}/api/v1/getFinancialReports`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          setData(result);
          setError(null);
        } else {
          throw new Error(result.message || "Failed to fetch data");
        }
      } catch (err) {
        console.error("Error fetching financial data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API_BASE_URL]);

  const excelDateToJS = (serial) => {
    if (!serial) return "N/A";
    const excelStartDate = new Date(1900, 0, 1);
    const jsDate = new Date(
      excelStartDate.getTime() + (serial - 2) * 24 * 60 * 60 * 1000
    );
    return jsDate.toLocaleDateString();
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "$0";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return "0%";
    return `${(value * 100).toFixed(1)}%`;
  };

  if (loading) {
    return (
      <Layout>
        <ThemeProvider theme={theme}>
          <Box
            sx={{
              minHeight: "100vh",
              background: "linear-gradient(135deg, #F8EEDB 0%, #FFFAF0 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  textAlign: "center",
                  borderRadius: 4,
                }}
              >
                <CircularProgress size={60} sx={{ color: "#EB5A3C", mb: 2 }} />
                <Typography variant="h6" color="text.primary">
                  Loading Investment Data...
                </Typography>
              </Paper>
            </motion.div>
          </Box>
        </ThemeProvider>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <ThemeProvider theme={theme}>
          <Box
            sx={{
              minHeight: "100vh",
              background: "linear-gradient(135deg, #F8EEDB 0%, #FFFAF0 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Alert
                severity="error"
                sx={{
                  maxWidth: 600,
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Error loading investment data
                </Typography>
                <Typography variant="body2">{error}</Typography>
              </Alert>
            </motion.div>
          </Box>
        </ThemeProvider>
      </Layout>
    );
  }

  if (!data?.success || !data?.data?.reports?.Investment_Sedule) {
    return (
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            minHeight: "100vh",
            background: "linear-gradient(135deg, #F8EEDB 0%, #FFFAF0 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Alert severity="warning">No investment data available</Alert>
        </Box>
      </ThemeProvider>
    );
  }

  const investmentData =
    data.data.reports.Investment_Sedule?.data?.Sheet1 || [];

  // Calculate summary statistics
  const totalInvestment = investmentData.reduce(
    (sum, item) => sum + (item["Investment Amount"] || 0),
    0
  );
  const totalMaturityValue = investmentData.reduce(
    (sum, item) => sum + (item["Expected Maturity Value"] || 0),
    0
  );
  const totalExpectedReturn = totalMaturityValue - totalInvestment;
  const averageReturn =
    investmentData.reduce(
      (sum, item) => sum + (item["Expected Annual Return (%)"] || 0),
      0
    ) / investmentData.length;

  // Prepare chart data
  const investmentAmountData = {
    labels: investmentData.map((item) => item["Investment Name"]),
    datasets: [
      {
        label: "Investment Amount",
        data: investmentData.map((item) => item["Investment Amount"]),
        backgroundColor: "rgba(235, 90, 60, 0.8)",
        borderColor: "#EB5A3C",
        borderWidth: 1,
      },
    ],
  };

  const returnComparisonData = {
    labels: investmentData.map((item) => item["Investment Name"]),
    datasets: [
      {
        label: "Investment Amount",
        data: investmentData.map((item) => item["Investment Amount"]),
        backgroundColor: "rgba(235, 90, 60, 0.6)",
      },
      {
        label: "Expected Maturity Value",
        data: investmentData.map((item) => item["Expected Maturity Value"]),
        backgroundColor: "rgba(75, 192, 192, 0.6)",
      },
    ],
  };

  const returnRateData = {
    labels: investmentData.map((item) => item["Investment Name"]),
    datasets: [
      {
        label: "Expected Annual Return (%)",
        data: investmentData.map(
          (item) => item["Expected Annual Return (%)"] * 100
        ),
        backgroundColor: investmentData.map(
          (_, index) => `hsl(${(index * 137.5) % 360}, 70%, 60%)`
        ),
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Investment Analysis",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "right",
      },
      title: {
        display: true,
        text: "Expected Annual Returns Distribution",
      },
    },
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getReturnColor = (returnRate) => {
    if (returnRate >= 0.1) return "#4CAF50"; // Green for high returns
    if (returnRate >= 0.08) return "#FF9800"; // Orange for medium returns
    return "#F44336"; // Red for low returns
  };

  const sortedByReturn = [...investmentData].sort(
    (a, b) =>
      (b["Expected Annual Return (%)"] || 0) -
      (a["Expected Annual Return (%)"] || 0)
  );

  return (
    <Layout>
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            minHeight: "100vh",
            background: "linear-gradient(135deg, #F8EEDB 0%, #FFFAF0 100%)",
            p: 3,
          }}
        >
          <Container maxWidth="xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Typography
                variant="h4"
                gutterBottom
                sx={{ color: "primary.main", mb: 4, textAlign: "center" }}
              >
                Investment Portfolio Dashboard
              </Typography>

              {/* Summary Cards */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Card>
                      <CardContent>
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 2 }}
                        >
                          <Avatar sx={{ bgcolor: "primary.main", mr: 2 }}>
                            <DollarSign />
                          </Avatar>
                          <Typography variant="h6" color="text.secondary">
                            Total Investment
                          </Typography>
                        </Box>
                        <Typography variant="h4" color="primary.main">
                          {formatCurrency(totalInvestment)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Card>
                      <CardContent>
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 2 }}
                        >
                          <Avatar sx={{ bgcolor: "#4CAF50", mr: 2 }}>
                            <TrendingUp />
                          </Avatar>
                          <Typography variant="h6" color="text.secondary">
                            Expected Maturity Value
                          </Typography>
                        </Box>
                        <Typography variant="h4" color="#4CAF50">
                          {formatCurrency(totalMaturityValue)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Card>
                      <CardContent>
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 2 }}
                        >
                          <Avatar sx={{ bgcolor: "#FF9800", mr: 2 }}>
                            <Target />
                          </Avatar>
                          <Typography variant="h6" color="text.secondary">
                            Expected Returns
                          </Typography>
                        </Box>
                        <Typography variant="h4" color="#FF9800">
                          {formatCurrency(totalExpectedReturn)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Card>
                      <CardContent>
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 2 }}
                        >
                          <Avatar sx={{ bgcolor: "#9C27B0", mr: 2 }}>
                            <Assessment />
                          </Avatar>
                          <Typography variant="h6" color="text.secondary">
                            Average Return Rate
                          </Typography>
                        </Box>
                        <Typography variant="h4" color="#9C27B0">
                          {formatPercent(averageReturn)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              </Grid>

              {/* Tabs */}
              <Paper sx={{ mb: 3 }}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  indicatorColor="primary"
                  textColor="primary"
                  centered
                >
                  <Tab label="Overview" />
                  <Tab label="Analytics" />
                  <Tab label="Investment Table" />
                  <Tab label="Top Performers" />
                </Tabs>
              </Paper>

              <AnimatePresence mode="wait">
                {activeTab === 0 && (
                  <motion.div
                    key="overview"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Grid container spacing={3}>
                      <Grid item xs={12} lg={8}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" gutterBottom>
                              Investment vs Expected Returns
                            </Typography>
                            <Bar
                              data={returnComparisonData}
                              options={chartOptions}
                            />
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} lg={4}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" gutterBottom>
                              Return Rate Distribution
                            </Typography>
                            <Doughnut
                              data={returnRateData}
                              options={doughnutOptions}
                            />
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </motion.div>
                )}

                {activeTab === 1 && (
                  <motion.div
                    key="analytics"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" gutterBottom>
                              Investment Amount Distribution
                            </Typography>
                            <Bar
                              data={investmentAmountData}
                              options={chartOptions}
                            />
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </motion.div>
                )}

                {activeTab === 2 && (
                  <motion.div
                    key="table"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Investment Details
                        </Typography>
                        <TableContainer>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Investment Name</TableCell>
                                <TableCell>Investment Date</TableCell>
                                <TableCell>Maturity Date</TableCell>
                                <TableCell align="right">
                                  Investment Amount
                                </TableCell>
                                <TableCell align="center">
                                  Return Rate
                                </TableCell>
                                <TableCell align="right">
                                  Expected Value
                                </TableCell>
                                <TableCell align="right">
                                  Expected Profit
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {investmentData.map((investment, index) => (
                                <TableRow key={index} hover>
                                  <TableCell>
                                    {investment["Investment Name"]}
                                  </TableCell>
                                  <TableCell>
                                    {excelDateToJS(
                                      investment["Investment Date"]
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {excelDateToJS(investment["Maturity Date"])}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatCurrency(
                                      investment["Investment Amount"]
                                    )}
                                  </TableCell>
                                  <TableCell align="center">
                                    <Chip
                                      label={formatPercent(
                                        investment["Expected Annual Return (%)"]
                                      )}
                                      sx={{
                                        bgcolor: getReturnColor(
                                          investment[
                                            "Expected Annual Return (%)"
                                          ]
                                        ),
                                        color: "white",
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatCurrency(
                                      investment["Expected Maturity Value"]
                                    )}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatCurrency(
                                      investment["Expected Maturity Value"] -
                                        investment["Investment Amount"]
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}

                {activeTab === 3 && (
                  <motion.div
                    key="performers"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Top Performing Investments
                        </Typography>
                        <List>
                          {sortedByReturn
                            .slice(0, 10)
                            .map((investment, index) => (
                              <React.Fragment key={index}>
                                <ListItem>
                                  <ListItemAvatar>
                                    <Avatar
                                      sx={{
                                        bgcolor: getReturnColor(
                                          investment[
                                            "Expected Annual Return (%)"
                                          ]
                                        ),
                                      }}
                                    >
                                      {index + 1}
                                    </Avatar>
                                  </ListItemAvatar>
                                  <ListItemText
                                    primary={investment["Investment Name"]}
                                    secondary={
                                      <Box>
                                        <Typography
                                          variant="body2"
                                          component="span"
                                        >
                                          Investment:{" "}
                                          {formatCurrency(
                                            investment["Investment Amount"]
                                          )}
                                        </Typography>
                                        <br />
                                        <Typography
                                          variant="body2"
                                          component="span"
                                        >
                                          Return Rate:{" "}
                                          {formatPercent(
                                            investment[
                                              "Expected Annual Return (%)"
                                            ]
                                          )}
                                        </Typography>
                                        <br />
                                        <Typography
                                          variant="body2"
                                          component="span"
                                        >
                                          Expected Value:{" "}
                                          {formatCurrency(
                                            investment[
                                              "Expected Maturity Value"
                                            ]
                                          )}
                                        </Typography>
                                      </Box>
                                    }
                                  />
                                  <Chip
                                    icon={<TrendingUp />}
                                    label={`+${formatCurrency(
                                      investment["Expected Maturity Value"] -
                                        investment["Investment Amount"]
                                    )}`}
                                    color="success"
                                    variant="outlined"
                                  />
                                </ListItem>
                                {index < 9 && <Divider />}
                              </React.Fragment>
                            ))}
                        </List>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </Container>
        </Box>
      </ThemeProvider>
    </Layout>
  );
};

export default InvestmentSedule;
